/**
 * SignupPage (B1.2 — Lovable composite wrapper).
 *
 * Smart wrapper that:
 *   1. Owns state via `useOnboarding()` (ADR-108 §6 draft + provision mutation)
 *   2. Translates feature-domain draft / seed into the Lovable primitive
 *      contract (`shared/ui/onboarding`) through `lovable-adapter`
 *   3. Renders the premium Lovable `OnboardingWizard` composite
 *
 * Authority:
 *   - ADR-108 (Tenant Onboarding Automation)
 *   - ADR-106 (Business Brand Separation) — biz-* tokens enforced
 *   - UX Blueprint §B1.2
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  OnboardingWizard,
  onboardingLabelsEn,
  onboardingLabelsTr,
} from '@/shared/ui/onboarding';
import type {
  OnboardingApiError as LovableOnboardingApiError,
  OnboardingLabels,
} from '@/shared/ui/onboarding/types';
import {
  OnboardingApiError,
  SUB_VERTICAL_SEED,
  useOnboarding,
} from '@/features/onboarding';
import {
  fromLovableBranchPatch,
  toLovableDraft,
  toLovableSubVerticalOptions,
} from '@/features/onboarding/lovable-adapter';

export function SignupPage(): JSX.Element {
  const { t, i18n } = useTranslation('onboarding');
  const navigate = useNavigate();
  const ob = useOnboarding();
  const [password, setPassword] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  const locale: 'tr' | 'en' = i18n.language.startsWith('en') ? 'en' : 'tr';

  const labels: OnboardingLabels = useMemo(
    () => (locale === 'en' ? onboardingLabelsEn : onboardingLabelsTr),
    [locale],
  );

  const subVerticalOptions = useMemo(
    () => toLovableSubVerticalOptions(SUB_VERTICAL_SEED),
    [],
  );

  const lovableDraft = useMemo(() => toLovableDraft(ob.draft), [ob.draft]);

  const lovableError: LovableOnboardingApiError | null = useMemo(() => {
    if (!ob.provisioningError) return null;
    return {
      code: ob.provisioningError.code,
      message: ob.provisioningError.message,
      fieldErrors: ob.provisioningError.violations.map((v) => ({
        path: v.path,
        message: v.message,
      })),
    };
  }, [ob.provisioningError]);

  const handleSubmit = useCallback(async () => {
    try {
      await ob.provision(password);
      setIsComplete(true);
      toast.success(t('success.title', { defaultValue: labels.success.title }));
    } catch (err) {
      if (err instanceof OnboardingApiError) {
        toast.error(
          t(`errors.${err.code}`, {
            defaultValue: labels.errors.provisioning_failed,
          }),
        );
      } else {
        toast.error(labels.errors.network);
      }
    }
  }, [ob, password, t, labels]);

  const handleContinueToDashboard = useCallback(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return (
    <OnboardingWizard
      step={ob.step}
      draft={lovableDraft}
      password={password}
      canAdvance={ob.canAdvance}
      isProvisioning={ob.isProvisioning}
      isComplete={isComplete}
      stepErrors={ob.stepErrors}
      provisioningError={lovableError}
      subVerticalOptions={subVerticalOptions}
      labels={labels}
      onSetStep={ob.setStep}
      onUpdateAccount={ob.updateAccount}
      onUpdatePassword={setPassword}
      onUpdateBusiness={ob.updateBusiness}
      onUpdateBranch={(patch) => ob.updateBranch(fromLovableBranchPatch(patch))}
      onNext={ob.nextStep}
      onBack={ob.previousStep}
      onSubmit={handleSubmit}
      onLogin={handleLogin}
      onContinueToDashboard={handleContinueToDashboard}
    />
  );
}
